import { PricedItem, PriceType } from "./economy-models";
import { ArmourType, BaseTypeTier, CustomModItemType, DefenceType, EndgameContent, IdentifiedItemType, JewelleryType, JewelType, ModDomain, ModItemCategoryType, Rarity, WeaponType } from "./enums";
import { EquipmentType } from "./equipment-type.enum";
import { IdentifiedItemConfig } from "./filter-models";
import { RuleId } from "./rule-id";

export interface ItemDataBase {
  itemType: string;
  itemClass: string;
  filterItemClass?: string;
  currencySearchName: string;
  imageBaseUrl?: string;
  tierListItemType?: string;
  content?: EndgameContent;
  isCurrency: boolean;
  isGear?: boolean;
  isWeapon?: boolean;
  isArmour?: boolean;
  isShield?: boolean;
}

export interface ItemDataInput extends ItemDataBase {
  baseTypes: string[];
}

export interface ItemData extends ItemDataBase {
  baseTypes: BaseType[];
}

export enum UniqueCategory {
  Weapons = 'Weapons',
  Armour = 'Armour',
  Jewellery = 'Jewellery',
  Jewels = 'Jewels',
  Flasks = 'Flasks',
  Maps = 'Maps',
  Other = 'Other',
}

export interface UniqueItem {
  name: string;
  category: UniqueCategory;
  baseType: string;
  image?: string;
  slots?: number;
  dustValue?: number;
  dustValuePerSlot?: number;
  // PoE1 only: 0 (rarest) to 5 (most common)
  uniqueTier?: number;
}

export interface UniqueItemDetails extends UniqueItem {
  implicitMods: string[];
  explicitMods: string[];
  flavorText?: string | null;
};

export interface FlatItemData {
  itemType: string;
  itemClass: string;
  baseType: BaseType;
};

export interface BaseType {
  name: string;
  image?: string;
  restricted?: string;
  restrictedContent?: EndgameContent;
}

export interface UniqueBossDropEntry {
  boss: string;
  bossLabel?: string;
  content?: EndgameContent;
  baseTypes: string[];
}

export interface RestrictedDropEntry {
  type: string;
  content?: EndgameContent;
  baseTypes: string[];
}

export enum Tier {
  SPurple = 'S',
  ARed = 'A',
  BOrange = 'B',
  CYellow = 'C',
  DWhite = 'D',
  EVisible = 'E',
  FHidden = 'F',
}

export type UniqueTierType = 'Excellent' | 'Good' | 'Potential' | 'Restricted' | 'Class' | 'Other';
export interface TieredUnique {
  baseType: string;
  tier: UniqueTierType;
  originalTier?: UniqueTierType;

  classSpecific?: string;
  category?: UniqueCategory;
  allUniques: PricedItemWithImage[];
  nonRestrictedUniques: PricedItemWithImage[];
  averageValue: number;
  lowestValue: number;
  manualTier?: boolean;

  previousAverageValue?: number;
  previousTier?: UniqueTierType;
  movedUp?: number;
  movedDown?: number;

  highDustValue?: boolean;
  averageNonRestrictedDustValue?: number;
  minNonRestrictedDustValue?: number;

}
export interface CustomTieredUnique { baseType: string, tier: UniqueTierType };
export interface PricedItemWithImage extends PricedItem {
  image?: string;
  bossDrop?: string;
  bossDropContent?: EndgameContent;
  dustValue?: number;
  slots?: number;
};

export type ChanceBaseTierType = 'Searching' | 'Potential' | 'Class' | 'Other';
export interface TieredChanceBase {
  baseType: string;
  tier: ChanceBaseTierType;
  originalTier?: ChanceBaseTierType;
  classSpecific?: string;
  uniques: PricedItemWithImage[]; // TODO: we probably also want to use ItemValueWithImage here
}
export interface CustomTieredChanceBase { baseType: string, tier: ChanceBaseTierType };

export type GearTierType = 'Basic' | 'Advanced' | 'Expert' | 'Elite' | 'Master';
export interface TieredGear {
  name: string;
  tier: GearTierType;
  originalTier?: GearTierType;
  itemClass: string;
  defenceType?: DefenceType;
  dropLvl?: number;
}
export interface CustomTieredGear { baseType: string, tier: GearTierType };

export type JewelleryTierType = 'Excellent' | 'Good' | 'Special' | 'Other';
export interface TieredJewellery {
  name: string;
  tier: JewelleryTierType;
  originalTier?: JewelleryTierType;
  itemClass: 'Amulets' | 'Rings' | 'Belts';
}
export interface CustomTieredJewellery { baseType: string, tier: JewelleryTierType };

export type EquipmentSelectionType = 'my' | 'other' | 'gems' | 'maps' | 'items';
export type EquipmentSlotType = 'gear' | 'jewellery' | 'jewel' | 'flask' | 'charm' | 'other';
export type EquipmentCategoryType = 'regular' | 'rare' | 'magic' | 'crafting' | 'identified' | 'leveling' | 'salvaging' | 'other';

export interface Equipment {
  equipmentType: EquipmentType;
  selection: EquipmentSelectionType;
  slot: EquipmentSlotType;
  category: EquipmentCategoryType;
  displayName: string;
  tieredTier: Tier;
  subtleTier?: Tier;
  hiddenWhenDisabled?: boolean;
  visibleWhenDisabled?: boolean;
  ruleId: RuleId;
  explainerIds?: string[];
}

export interface TieredEquipment {
  equipmentType: EquipmentType;
  selection: EquipmentSelectionType;
  slot: EquipmentSlotType;
  category: EquipmentCategoryType;
  displayName: string;
  tier: Tier;
  originalTier?: Tier;
}

export interface CustomTieredEquipment { equipmentType: string, tier: Tier };

export type TierThresholdValue = number | string;

export type TierValueList = TierValue[];
export interface TierValue { tier: Tier, value: TierThresholdValue, baseCurrency: 'Chaos Orb' | 'Exalted Orb' | 'Divine Orb' };

export type UniquesTierValueList = UniquesTierValue[];
export interface UniquesTierValue { tier: 'Excellent' | 'Good' | 'Other', value: TierThresholdValue, baseCurrency: 'Chaos Orb' | 'Exalted Orb' | 'Divine Orb' };

export type ClusterJewelTierValueList = ClusterJewelTierValue[];
export interface ClusterJewelTierValue { tier: 'Excellent' | 'Good' | 'Other', value: TierThresholdValue, baseCurrency: 'Chaos Orb' | 'Exalted Orb' | 'Divine Orb' };

export type TierList = TierData[];

export interface TierData {
  tier: Tier;
  itemTypes: TierItemTypeGroup[];
};

export interface TierItemTypeGroup {
  itemType: string;
  baseTypes: TieredItem[];
};

export interface TieredItem {
  name: string;
  value?: number; // SSF items don't have a value
  tier: Tier;
  originalTier?: Tier;

  hiddenByQuickFilter?: boolean;
  divCard?: DivCard;
  equipment?: Equipment;
  noValue?: boolean;

  priceType?: PriceType;
  manualTier?: boolean;
  favouriteCurrencyAdjusted?: boolean;

  previousValue?: number;
  previousTier?: Tier;
  movedUp?: number;
  movedDown?: number;
};

export interface CustomTieredItem { name: string, tier: Tier, shownExplicitly?: boolean };

export interface ItemDescription {
  baseType: string;
  gemDescription?: string;
  description?: string;
  effects?: string[];
}

export interface JewelleryData {
  name: string;

  lvl?: number;
  str?: number;
  dex?: number;
  int?: number;

  implicits: string[];
}

export interface FlaskData {
  name: string;
  effects: string[];
}

export interface GearData {
  lvl?: number;
  str?: number;
  dex?: number;
  int?: number;

  job?: string; // For Heist items

  name: string;

  minDmg?: number;
  maxDmg?: number;
  crit?: number;
  aps?: number;
  rt?: number;

  block?: number;

  arm?: number;
  es?: number;
  ev?: number;

  implicits: string[];

  dropLvl?: number;
}

export interface IlvlPriceData {
  price: number | null;
  amt: number;
}

export interface GearValuesData {
  baseType: string;
  itemType: string;
  ilvls: Record<string, IlvlPriceData>;
}

export interface JewelleryValuesData {
  baseType: string;
  category: string;
  ilvls: Record<string, IlvlPriceData>;
}

export interface NinjaBaseTypeEntry {
  baseType: string;
  percentage: number;
}

export interface NinjaBaseTypesData {
  game: string;
  leagueSlug: string;
  snapshot: string;
  scrapedAt: string;
  weapons: Record<string, NinjaBaseTypeEntry[]>;
  armour: Record<string, Record<string, NinjaBaseTypeEntry[]>>;
}

export interface DivCard {
  name: string;
  stackSize: number;
  artFileName: string;
  text?: string;
  flavourText?: string;
  chaosValue: number;
  dropInfo?: DivCardDropInfo;
}

export interface DivCardDropInfo {
  areas?: string[];
  minLevel?: number;
  monsters?: string[];
  text?: string;
  weight?: number;
  dropsPerMap?: number;
}

export interface GemData {
  name: string;
  tags?: string[];
  description?: string;
}

export type HeistLocation = 'Bunker' | 'Records Office' | 'Mansion' | 'Smuggler\'s Den' | 'Underbelly' | 'Laboratory' | 'Prohibited Library' | 'Repository' | 'Tunnels';
export type HeistSkill = 'agility' | 'counter-thaumaturgy' | 'engineering' | 'demolition' | 'lockpicking' | 'perception' | 'trap-disarmament' | 'brute-force' | 'deception';
export type HeistReward = 'currency' | 'fragments' | 'gems' | 'jewellery' | 'harbinger' | 'divination' | 'armour' | 'weapons' | 'breach' | 'abyss' | 'legion' | 'talisman' | 'maps' | 'uniques' | 'essence' | 'random' | 'ultimatum' | 'delirium' | 'blight' | 'delve';

export type ModTierType = 'Perfect' | 'Great' | 'Good' | 'Neutral' | 'Bad';
export type AffixType = 'Prefix' | 'Suffix';

export interface Mod {
  id: number;
  domain: ModDomain;
  affixType: AffixType;
  type: string;
  lvl: number;
  name: string;
  text: string;

  slots: ModSlot[];
  exSlots: ModSlot[];
  tags: ModTag[];

  // Optional properties for pattern matching (used by ModMatcherService)
  pattern?: RegExp;
  ranges?: Array<{ min: number; max: number }>;
  patternKey?: string;
}

export interface SlotTierData {
  tier: number;
  slots: string[];
}

export interface ModSlotData {
  slot: string;
  modTypes: string[];
}

export interface ModSlot {
  id: string;
  techName: string;
  name: string;
  icon?: string;
  itemType?: string;
  exampleBaseTypeImage?: string;
}

export interface ModTag {
  id: string;
  techName: string;
  name: string;
  abbr: string;
  color: string;
}

export interface ModItemCategory {
  domain: ModDomain;
  type: IdentifiedItemType;
  category: ModItemCategoryType;
  name: string;
  tags: string[];
  excludeTags: string[];
  badTags: string[];
  slots: string[];
  excludeSlots: string[];
  includePerfectModTypes?: string[];
  includeGreatModTypes: string[];
  includeGoodModTypes: string[];
  excludeModTypes: string[];

  baseTypes?: string[];

  fixedPerfectMods?: string[];
  fixedGreatMods?: string[];
  fixedGoodMods?: string[];
  fixedBadMods?: string[];
}

export interface TieredMod {
  name: string,
  affixType: AffixType,
  tier: ModTierType,
  cat?: ModItemCategoryType | string,
  originalTier?: ModTierType

  mods?: Mod[];
};

// TODO: do I want to keep this here? Maybe calculate the other properties in the tiering service?
export interface ModTierItemVm extends TieredMod {
  primaryMod?: Mod;
  applicableMods?: Mod[];
  nonApplicableMods?: Mod[];
  relevantMods?: Mod[];
  nonRelevantMods?: Mod[];
  possiblyIncorrectSlot?: boolean;
  noApplicableTag?: boolean;
}

export class CustomModItemCategory {
  constructor(init: Partial<CustomModItemCategory>) {
    Object.assign(this, init);
  }

  name: string = '';
  itemType: CustomModItemType = CustomModItemType.Any;
  weaponClasses: WeaponType[] = [WeaponType.All];
  armourTypes: ArmourType[] = [ArmourType.All];
  defenceTypes: DefenceType[] = [DefenceType.All];
  minimumGearTier: BaseTypeTier = BaseTypeTier.Basic;
  jewelleryClasses: JewelleryType[] = [JewelleryType.All];
  jewelBaseTypes: JewelType[] = [JewelType.All];

  // minimumRarity: Rarity = Rarity.Rare;
  // singleMod?: boolean;

  customConfiguration?: IdentifiedItemConfig;
}

export interface ClusterJewelTiers {
  excellent: ClusterJewelBase[];
  good: ClusterJewelBase[];
}

export interface ClusterJewelBase {
  minItemLevel?: number;
  maxItemLevel?: number;
  baseType: string;
  passiveNum?: number;
  passiveNodes?: string[];
}
